/**
 * Drag-drop statement upload (§16 Phase 2).
 *
 * §1 calls this "an equal, first-class ingestion path" — not a fallback. It's
 * how history gets in, and how any bank that mails a portal link instead of an
 * attachment gets handled. So it earns real estate and real feedback, rather
 * than being a hidden button.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileUp, Loader2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/primitives";
import { statementsApi, type UploadResult } from "@/lib/api/client";
import { cn } from "@/lib/cn";

export function UploadDropzone({ onUploaded }: { onUploaded?: (r: UploadResult) => void }) {
  const qc = useQueryClient();
  const [dragging, setDragging] = useState(false);
  const [queue, setQueue] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: (file: File) => statementsApi.upload(file),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["statements"] });
      qc.invalidateQueries({ queryKey: ["review-queue"] });
      qc.invalidateQueries({ queryKey: ["cards"] });

      // Each outcome gets its own voice — "uploaded!" on a statement that
      // silently needs review would be a lie of omission (§2.3).
      if (result.duplicate) {
        toast.info("Already have this one", { description: result.message });
      } else if (!result.ok) {
        toast.warning("Needs your input", { description: result.message });
      } else if (result.needs_review) {
        toast.warning(`Read ${result.transaction_count} transactions — please check`, {
          description: result.warnings?.[0] ?? result.message,
        });
      } else {
        toast.success(result.message, {
          description: result.parser_name ? `Read by the ${result.parser_name} parser.` : undefined,
        });
      }
      onUploaded?.(result);
    },
    onError: (e: Error, file) => {
      toast.error(`Couldn't read ${file.name}`, { description: e.message });
    },
    onSettled: (_d, _e, file) => {
      setQueue((q) => q.filter((n) => n !== file.name));
    },
  });

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files?.length) return;
      const pdfs = Array.from(files).filter((f) => f.name.toLowerCase().endsWith(".pdf"));
      const rejected = files.length - pdfs.length;
      if (rejected > 0) {
        toast.error(
          rejected === 1 ? "That isn't a PDF" : `${rejected} files weren't PDFs`,
          { description: "MyDeck reads the statement PDF your bank emails you." },
        );
      }
      // Sequential, not parallel: two statements for the same card racing each
      // other can both miss the other's dedupe check.
      setQueue((q) => [...q, ...pdfs.map((f) => f.name)]);
      void pdfs.reduce(
        (chain, file) => chain.then(() => upload.mutateAsync(file).catch(() => undefined)),
        Promise.resolve<unknown>(undefined),
      );
    },
    [upload],
  );

  const busy = upload.isPending || queue.length > 0;

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={cn(
        "rounded-xl border border-dashed p-8 text-center transition-colors",
        dragging ? "border-brass bg-brass/5" : "border-hairline-strong bg-panel",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="sr-only"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="flex flex-col items-center gap-3">
        <div className="rounded-full border border-hairline bg-raised p-3">
          {busy ? (
            <Loader2 className="h-5 w-5 animate-spin text-brass" />
          ) : (
            <FileUp className="h-5 w-5 text-faint" />
          )}
        </div>

        <div className="space-y-1">
          <h3 className="font-display text-base font-medium text-ink">
            {busy ? `Reading ${queue.length} statement${queue.length === 1 ? "" : "s"}…` : "Drop a statement PDF"}
          </h3>
          <p className="mx-auto max-w-md text-sm text-muted">
            {busy
              ? "Decrypting and reading the transaction table."
              : "MyDeck unlocks it with the password saved on the card, reads the totals and every transaction, and flags anything it isn't sure about."}
          </p>
        </div>

        {!busy && (
          <Button variant="default" size="sm" onClick={() => inputRef.current?.click()}>
            Choose a file
          </Button>
        )}
      </div>
    </div>
  );
}
